import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { VehicleType } from "../../types/deadLeg";

function toLocalDatetimeValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DeadLegForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const defaultUntil = new Date();
  defaultUntil.setDate(defaultUntil.getDate() + 7);

  const [form, setForm] = useState({
    atPostCode: "",
    atRadiusMiles: "2",
    goingTo: "Anywhere",
    fromDateTime: toLocalDatetimeValue(new Date()),
    untilDateTime: toLocalDatetimeValue(defaultUntil),
    discountPercent: "10",
    linkedFullPriceBookingRef: "",
    vehicleTypeId: "",
    remarks: "",
  });

  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axiosInstance.get<VehicleType[]>("/vehicletypes").then((res) => {
      setVehicleTypes(res.data);
      const base = res.data.find((v) => (v as { isMileageBase?: boolean }).isMileageBase);
      setForm((f) => ({
        ...f,
        vehicleTypeId: f.vehicleTypeId || base?.id || res.data[0]?.id || "",
      }));
    });
    if (isEditMode) {
      axiosInstance.get(`/deadlegs/${id}`).then((res) => {
        const d = res.data;
        setForm({
          atPostCode: d.atPostCode ?? "",
          atRadiusMiles: String(d.atRadiusMiles ?? 2),
          goingTo: d.goingTo ?? "Anywhere",
          fromDateTime: d.from ? toLocalDatetimeValue(new Date(d.from)) : "",
          untilDateTime: d.until ? toLocalDatetimeValue(new Date(d.until)) : "",
          discountPercent: String(d.discountPercent ?? 10),
          linkedFullPriceBookingRef: d.linkedFullPriceBookingRef ?? "",
          vehicleTypeId: d.vehicleTypeId ?? "",
          remarks: d.remarks ?? "",
        });
      });
    }
  }, [id, isEditMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        atPostCode: form.atPostCode,
        atRadiusMiles: Number(form.atRadiusMiles),
        goingTo: form.goingTo || "Anywhere",
        fromDateTime: new Date(form.fromDateTime).toISOString(),
        untilDateTime: new Date(form.untilDateTime).toISOString(),
        discountPercent: Number(form.discountPercent),
        linkedFullPriceBookingRef: form.linkedFullPriceBookingRef || null,
        vehicleTypeId: form.vehicleTypeId || null,
        remarks: form.remarks,
      };

      if (isEditMode) {
        await axiosInstance.put(`/deadlegs/${id}`, payload);
      } else {
        await axiosInstance.post("/deadlegs", payload);
      }
      navigate("/dead-legs");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to save";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 w-full">
      <PageBreadcrumb
        pageTitle={isEditMode ? "Edit Dead Leg Discount" : "Add Dead Leg Discount"}
      />

      <p className="mt-4 text-sm text-gray-600">
        Create a discount for empty return legs near a postcode. Customers booking within the
        radius and time window receive the discount percentage off their fare.
      </p>

      <form
        onSubmit={handleSubmit}
        className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6 space-y-4"
      >
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle type</label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.vehicleTypeId}
            onChange={(e) => setForm((f) => ({ ...f, vehicleTypeId: e.target.value }))}
          >
            <option value="">All vehicle types</option>
            {vehicleTypes.map((v) => (
              <option key={v.id} value={v.id}>
                {v.description}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">At (postcode)</label>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500">Within</span>
            <input
              type="number"
              min="0"
              step="0.1"
              className="w-20 border rounded-lg px-2 py-2"
              value={form.atRadiusMiles}
              onChange={(e) => setForm((f) => ({ ...f, atRadiusMiles: e.target.value }))}
              required
            />
            <span className="text-sm text-gray-500">miles of</span>
            <input
              className="flex-1 min-w-[140px] border rounded-lg px-3 py-2 uppercase"
              placeholder="WA5 8PS"
              value={form.atPostCode}
              onChange={(e) => setForm((f) => ({ ...f, atPostCode: e.target.value }))}
              required
            />
          </div>
        </div>

        <InputWithLabel
          label="Going to"
          name="goingTo"
          value={form.goingTo}
          onChange={(e) => setForm((f) => ({ ...f, goingTo: e.target.value }))}
        />

        <InputWithLabel
          label="From"
          name="fromDateTime"
          type="datetime-local"
          value={form.fromDateTime}
          onChange={(e) => setForm((f) => ({ ...f, fromDateTime: e.target.value }))}
          required
        />

        <InputWithLabel
          label="Until"
          name="untilDateTime"
          type="datetime-local"
          value={form.untilDateTime}
          onChange={(e) => setForm((f) => ({ ...f, untilDateTime: e.target.value }))}
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            className="w-full border rounded-lg px-3 py-2"
            value={form.discountPercent}
            onChange={(e) => setForm((f) => ({ ...f, discountPercent: e.target.value }))}
            required
          />
        </div>

        <InputWithLabel
          label="Linked to full price booking ref (optional)"
          name="linkedFullPriceBookingRef"
          value={form.linkedFullPriceBookingRef}
          onChange={(e) =>
            setForm((f) => ({ ...f, linkedFullPriceBookingRef: e.target.value }))
          }
          placeholder="e.g. 1626630311-9617"
        />

        <InputWithLabel
          label="Remarks"
          name="remarks"
          value={form.remarks}
          onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
        />

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2 bg-brand-500 text-white rounded-lg disabled:opacity-50"
          >
            {loading ? "Saving..." : isEditMode ? "Update" : "+ Add"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/dead-legs")}
            className="px-4 py-2 border rounded-lg text-gray-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
